/**
 * Tableau de bord back-office (admin + super_admin) — vue d'ensemble
 * chiffrée de toute l'app : nb clients, nb dossiers, documents à valider,
 * demandes partenaires (leads) en attente, RDV à venir, CA cumulé.
 */
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  BarChart3,
  CalendarClock,
  FileCheck2,
  Inbox,
  TrendingUp,
  Users,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DashboardLayout } from '@/components/dashboard/dashboard-layout';
import { getStats } from '@/features/admin/admin-api';
import { statusLabels } from '@/features/projects/labels';
import { useAuth } from '@/lib/auth-context';
import type { ProjectStatus } from '@bcl/shared';

function formatCurrency(n: number): string {
  return n.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
}

export function AdminOverviewPage() {
  const { user } = useAuth();
  const stats = useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: getStats,
  });

  const tiles = [
    {
      title: 'Clients',
      value: stats.data?.clients ?? '—',
      description: 'Comptes clients actifs',
      icon: Users,
      to: '/admin/clients',
    },
    {
      title: 'Dossiers',
      value: stats.data?.projects ?? '—',
      description: 'Dossiers tous statuts',
      icon: BarChart3,
      to: '/admin/projects',
    },
    {
      title: 'Documents à valider',
      value: stats.data?.pendingDocuments ?? '—',
      description: 'En attente de revue',
      icon: FileCheck2,
      to: '/admin/documents',
    },
    {
      title: 'Demandes partenaires',
      value: stats.data?.pendingLeads ?? '—',
      description: 'Leads à appeler / qualifier',
      icon: Inbox,
      to: '/admin/leads',
    },
    {
      title: 'RDV à venir',
      value: stats.data?.upcomingAppointments ?? '—',
      description: 'Interventions planifiées',
      icon: CalendarClock,
      to: '/admin/planning',
    },
    {
      title: 'CA cumulé',
      value: stats.data ? formatCurrency(stats.data.cumulativeRevenue) : '—',
      description: 'Paiements encaissés',
      icon: TrendingUp,
    },
  ];

  return (
    <DashboardLayout variant="admin">
      {user?.role === 'super_admin' && (
        <Card className="mb-6 border-bcl-blue/30 bg-bcl-blue/5">
          <CardHeader className="flex flex-row items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-bcl-blue" />
            <div>
              <CardTitle className="text-base">Mode super-admin</CardTitle>
              <CardDescription>
                Accès total à l&apos;application. Vous gérez aussi l&apos;équipe
                BCL dans <Link to="/admin/staff" className="font-semibold text-bcl-blue hover:underline">Équipe BCL</Link>.
              </CardDescription>
            </div>
          </CardHeader>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {tiles.map((tile) => {
          const card = (
            <Card className={tile.to ? 'transition-shadow group-hover:shadow-md' : ''}>
              <CardHeader className="flex flex-row items-start gap-3 pb-3">
                <div className="grid h-10 w-10 place-items-center rounded-md bg-bcl-blue/10 text-bcl-blue">
                  <tile.icon className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-sm font-medium text-gray-500">
                    {tile.title}
                  </CardTitle>
                  <p className="mt-1 text-2xl font-bold">{tile.value}</p>
                </div>
              </CardHeader>
              <CardContent className="pt-0 text-xs text-gray-500">
                {tile.description}
              </CardContent>
            </Card>
          );
          return tile.to ? (
            <Link key={tile.title} to={tile.to} className="group block">
              {card}
            </Link>
          ) : (
            <div key={tile.title} className="block">
              {card}
            </div>
          );
        })}
      </div>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Dossiers par statut</CardTitle>
          <CardDescription>Suivi de production en temps réel.</CardDescription>
        </CardHeader>
        <CardContent>
          {!stats.data || Object.keys(stats.data.projectsByStatus).length === 0 ? (
            <p className="text-sm text-gray-500">Aucun dossier pour le moment.</p>
          ) : (
            <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {(Object.keys(stats.data.projectsByStatus) as ProjectStatus[]).map((status) => (
                <li
                  key={status}
                  className="flex items-center justify-between rounded-md border bg-white p-3"
                >
                  <span className="text-sm">{statusLabels[status]}</span>
                  <span className="text-sm font-semibold text-bcl-blue">
                    {stats.data!.projectsByStatus[status]}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  );
}
